/**
 * RoutesDashboardPage - Routes platform dashboard with route metrics and recent routes
 */

import { Page, View, TableView } from 'web-mojo';
import { MetricsMiniChartWidget } from 'web-mojo/charts';
import { RouteList } from '@portal/models/Route.js';


class MetricsView extends View {
    constructor(options = {}) {
        super({ ...options, className: 'metrics-section' });
    }

    async getTemplate() {
        return `
        <div class="row g-3 mb-4">
            <div class="col-xl-2 col-lg-4 col-6">
                <div data-container="metric_routes_completed"></div>
            </div>
            <div class="col-xl-2 col-lg-4 col-6">
                <div data-container="metric_routes_cancelled"></div>
            </div>
            <div class="col-xl-2 col-lg-4 col-6">
                <div data-container="metric_stops_completed"></div>
            </div>
            <div class="col-xl-2 col-lg-4 col-6">
                <div data-container="metric_stops_skipped"></div>
            </div>
            <div class="col-xl-2 col-lg-4 col-6">
                <div data-container="metric_off_route"></div>
            </div>
            <div class="col-xl-2 col-lg-4 col-6">
                <div data-container="metric_stop_requests"></div>
            </div>
        </div>
        `;
    }

    async onInit() {
        const defaults = {
            textColor: "#FFFFFF",
            granularity: 'days',
            trendRange: 4,
            trendOffset: 1,
            account: 'group-',
            chartType: 'bar',
            showTooltip: true,
            showXAxis: false,
            height: 35,
            chartWidth: '100%',
            color: 'rgba(245, 245, 255, 0.8)',
            fill: true,
            fillColor: 'rgba(245, 245, 255, 0.5)',
            smoothing: 0.2,
            showTrending: true,
        };

        this.routeCompletedChart = new MetricsMiniChartWidget({
            ...defaults,
            icon: "bi bi-check-circle-fill",
            title: 'Routes Completed',
            subtitle: '{{total}} <span class="subtitle-label">{{total_label}}</span>',
            background: "#198754",
            slugs: ['route_completed'],
            containerId: 'metric_routes_completed'
        });
        this.addChild(this.routeCompletedChart);

        this.routeCancelledChart = new MetricsMiniChartWidget({
            ...defaults,
            icon: "bi bi-x-circle-fill",
            title: 'Routes Cancelled',
            subtitle: '{{total}} <span class="subtitle-label">{{total_label}}</span>',
            background: "#dc3545",
            slugs: ['route_cancelled'],
            containerId: 'metric_routes_cancelled'
        });
        this.addChild(this.routeCancelledChart);

        this.stopsCompletedChart = new MetricsMiniChartWidget({
            ...defaults,
            icon: "bi bi-geo-alt-fill",
            title: 'Stops Completed',
            subtitle: '{{total}} <span class="subtitle-label">{{total_label}}</span>',
            background: "#0d6efd",
            slugs: ['stop_completed'],
            containerId: 'metric_stops_completed'
        });
        this.addChild(this.stopsCompletedChart);

        this.stopsSkippedChart = new MetricsMiniChartWidget({
            ...defaults,
            icon: "bi bi-skip-forward-fill",
            title: 'Stops Skipped',
            subtitle: '{{total}} <span class="subtitle-label">{{total_label}}</span>',
            background: "#fd7e14",
            slugs: ['stop_skipped'],
            containerId: 'metric_stops_skipped'
        });
        this.addChild(this.stopsSkippedChart);

        this.offRouteChart = new MetricsMiniChartWidget({
            ...defaults,
            icon: "bi bi-exclamation-triangle-fill",
            title: 'Off-Route Alerts',
            subtitle: '{{total}} <span class="subtitle-label">{{total_label}}</span>',
            background: "#ffc107",
            textColor: "#212529",
            slugs: ['off_route_alert'],
            containerId: 'metric_off_route'
        });
        this.addChild(this.offRouteChart);

        this.stopRequestsChart = new MetricsMiniChartWidget({
            ...defaults,
            icon: "bi bi-hand-index-thumb-fill",
            title: 'Field Requests',
            subtitle: '{{total}} <span class="subtitle-label">{{total_label}}</span>',
            background: "#6f42c1",
            slugs: ['stop_request_created'],
            containerId: 'metric_stop_requests'
        });
        this.addChild(this.stopRequestsChart);
    }

    async onAfterMount() {
        this.refreshValues();
    }

    async refreshValues() {
        const group = this.getApp().getActiveGroup();
        const account = `group-${group.id}`;
        const allWidgets = [
            this.routeCompletedChart, this.routeCancelledChart,
            this.stopsCompletedChart, this.stopsSkippedChart,
            this.offRouteChart, this.stopRequestsChart,
        ];
        allWidgets.forEach(c => {
            if (!c) return;
            c.account = account;
            if (c.chartOptions) c.chartOptions.account = account;
        });
        await Promise.allSettled(allWidgets.map(c => c?.refresh()));
    }
}


export default class RoutesDashboardPage extends Page {
    constructor(options = {}) {
        super({
            ...options,
            title: 'Routes Dashboard',
            icon: "bi-speedometer2",
            className: 'dashboard-page'
        });
        this.pageSubtitle = 'Route operations overview';
    }

    async getTemplate() {
        return `
        <div class="dashboard-container container-lg">
            <div class="d-flex justify-content-between align-items-center mb-3">
                <div>
                    <p class="text-muted mb-0">{{pageSubtitle}}</p>
                </div>
                <button type="button" class="btn btn-outline-secondary btn-sm" data-action="refresh-all">
                    <i class="bi bi-arrow-clockwise me-1"></i>Refresh
                </button>
            </div>

            <div data-container="metrics"></div>

            <div style="background:#fff;border-radius:12px;box-shadow:0 1px 3px rgba(0,0,0,0.08);overflow:hidden;">
                <div style="padding:1rem 1.25rem;border-bottom:1px solid #f0f0f0;">
                    <h6 style="margin:0;font-weight:700;font-size:0.95rem;">
                        <i class="bi bi-signpost-split me-2"></i>Recent Routes
                    </h6>
                </div>
                <div data-container="recent-routes"></div>
            </div>
        </div>
        `;
    }

    async onInit() {
        this.metricsView = new MetricsView({ containerId: 'metrics' });
        this.addChild(this.metricsView);

        this.recentRoutes = new TableView({
            containerId: "recent-routes",
            Collection: RouteList,
            requiresActiveGroup: true,
            hideActivePillNames: ["group"],
            searchable: false,
            filterable: false,
            clickAction: "view",
            columns: [
                { label: "Date", key: "scheduled_date", formatter: "date" },
                { label: "Route", key: "template.name", formatter: "default('—')" },
                { label: "Operator", key: "operator.display_name", formatter: "default('—')" },
                { label: "Status", key: "status|badge" },
                { label: "Stops", key: "stop_count", formatter: "default('0')" },
            ],
        });
        this.addChild(this.recentRoutes);
    }

    async onEnter() {
        await super.onEnter();
        this.activeGroup = this.getApp().getActiveGroup();
        this.recentRoutes.collectionParams = {
            size: 10,
            sort: '-scheduled_date',
            group: this.activeGroup.id,
        };
    }

    async onActionRefreshAll() {
        await this.refreshDashboard();
        this.getApp().toast.success('Dashboard refreshed');
    }

    async refreshDashboard() {
        this.activeGroup = this.getApp().getActiveGroup();
        this.recentRoutes.collectionParams = {
            size: 10,
            sort: '-scheduled_date',
            group: this.activeGroup.id,
        };
        await Promise.allSettled([
            this.metricsView?.refreshValues(),
            this.recentRoutes?.collection?.fetch()
        ].filter(Boolean));
    }

    async onGroupChange(group) {
        this.refreshDashboard();
    }
}
